import { inject, Injectable } from '@angular/core';
import { PermissionState } from '@capacitor/core';
import { ActionPerformed, PushNotifications, Token } from '@capacitor/push-notifications';
import { DeviceInfoService } from '@services/device/device-info.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { PatientUserFcmTokenGQL, RemovePatientFcmTokenGQL, SavePatientUserFcmTokenGQL, SetPatientUserNotificationDefaultsGQL } from 'graphql/generated';
import { BehaviorSubject, defer, distinct, EMPTY, filter, map, Observable, of, switchMap, take } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class PushNotificationService {
    deviceInfoService: DeviceInfoService = inject(DeviceInfoService);
    savePatientUserFcmToken = inject(SavePatientUserFcmTokenGQL);
    setPatientUserNotificationsDefaults = inject(SetPatientUserNotificationDefaultsGQL);
    removePatientUserFcmToken = inject(RemovePatientFcmTokenGQL);
    getPatientUserFcmToken = inject(PatientUserFcmTokenGQL);
    private navService = inject(NavigationService);
    public actionPerformedViaPushNotification = false;

    fcmTokenSubject = new BehaviorSubject<string | null>(null);

    public InitRegistration() {
        return this.checkPermission().pipe(
            switchMap((permState) => {
                if (permState === 'prompt') {
                    return this.requestPermission();
                }
                return of(permState);
            }),
            switchMap((permState) => {
                if (permState === 'granted') {
                    return this.setFCMToken();
                }
                return EMPTY;
            })
        );
    }

    public openNativeSettings(): Observable<boolean> {
        return defer(() => NativeSettings.open({ optionAndroid: AndroidSettings.AppNotification, optionIOS: IOSSettings.App })).pipe(map((_) => true));
    }

    private requestPermission(): Observable<PermissionState> {
        return defer(() => PushNotifications.requestPermissions()).pipe(
            map((permission) => {
                return permission.receive;
            }),
            switchMap((permission) => {
                if (permission === 'granted') {
                    return this.setPatientUserNotificationsDefaults.mutate().pipe(map((x) => permission));
                }
                return of(permission);
            })
        );
    }

    private setFCMToken() {
        return defer(() => PushNotifications.register());
    }

    addFcmListeners() {
        PushNotifications.addListener('registration', (token: Token) => {
            this.fcmTokenSubject.next(token.value);
        });
        PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
            const url = action.notification?.data?.url;
            if (url) {
                this.actionPerformedViaPushNotification = true;
                this.navService.navigate([`/${url}`]);
            }
        });
    }

    saveFcmToken$ = this.fcmTokenSubject.pipe(
        filter(Boolean),
        distinct(),
        switchMap((token) =>
            this.deviceInfoService.getDeviceId().pipe(
                take(1),
                switchMap((deviceId) =>
                    this.getPatientUserFcmToken.fetch({ variables: { deviceId: deviceId }, fetchPolicy: 'no-cache' }).pipe(
                        switchMap((fcmTokenInfo) => {
                            if (!fcmTokenInfo.data) {
                                throw new Error('Failed to fetch FCM token info');
                            }
                            if (!!fcmTokenInfo.data.patientUserFcmToken) {
                                return this.savePatientUserFcmToken.mutate({
                                    variables: {
                                        dto: { deviceId: deviceId, fcmToken: token, id: +fcmTokenInfo.data.patientUserFcmToken.id },
                                    },
                                });
                            } else {
                                return this.savePatientUserFcmToken.mutate({
                                    variables: {
                                        dto: { deviceId: deviceId, fcmToken: token, id: 0 },
                                    },
                                });
                            }
                        })
                    )
                )
            )
        )
    );

    public clearExistingFCMToken() {
        return this.deviceInfoService.getDeviceId().pipe(
            switchMap((deviceId) => this.removePatientUserFcmToken.mutate({ variables: { deviceId: deviceId } })),
            switchMap(() => defer(() => PushNotifications.unregister())),
            switchMap(() => defer(() => PushNotifications.removeAllListeners()))
        );
    }

    public checkPermission(): Observable<PermissionState> {
        return defer(() => PushNotifications.checkPermissions()).pipe(map((permission) => permission.receive));
    }
}
